import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount || 0)
}

export function formatDate(date: string | Date | null | undefined, pattern = 'MMM d, yyyy'): string {
  if (!date) return '—'
  try {
    return format(new Date(date), pattern)
  } catch {
    return '—'
  }
}

export function formatRelativeDate(date: string | Date | null | undefined): string {
  if (!date) return '—'
  try {
    const d = new Date(date)
    if (isToday(d)) return formatDistanceToNow(d, { addSuffix: true })
    if (isYesterday(d)) return 'Yesterday'
    return format(d, 'MMM d')
  } catch {
    return '—'
  }
}

export function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toString()
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function slugify(str: string): string {
  return str.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
}

export function isOverdue(dueDate: string | null | undefined): boolean {
  if (!dueDate) return false
  return new Date(dueDate) < new Date()
}

// Themed status palette. Each entry's bg/text/border resolve to Layer 2
// tokens at render time, so status pills theme correctly across all three
// modes regardless of which page consumes them via getStatusColor().
export const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  // Conversation / general
  open: { bg: 'var(--positive-dim)', text: 'var(--positive)', border: 'var(--positive-border)' },
  pending: { bg: 'var(--warning-dim)', text: 'var(--warning)', border: 'var(--warning-border)' },
  resolved: { bg: 'var(--border-subtle)', text: 'var(--ink-tertiary)', border: 'var(--border-default)' },
  closed: { bg: 'var(--hover-bg)', text: 'var(--ink-tertiary)', border: 'var(--border-subtle)' },
  // Invoice
  draft: { bg: 'var(--border-subtle)', text: 'var(--ink-secondary)', border: 'var(--border-default)' },
  sent: { bg: 'var(--info-dim)', text: 'var(--info)', border: 'var(--info-border)' },
  paid: { bg: 'var(--positive-dim)', text: 'var(--positive)', border: 'var(--positive-border)' },
  overdue: { bg: 'var(--danger-dim)', text: 'var(--danger)', border: 'var(--danger-border)' },
  void: { bg: 'var(--hover-bg)', text: 'var(--ink-tertiary)', border: 'var(--border-subtle)' },
  // Deal
  won: { bg: 'var(--positive-dim)', text: 'var(--positive)', border: 'var(--positive-border)' },
  lost: { bg: 'var(--danger-dim)', text: 'var(--danger)', border: 'var(--danger-border)' },
  // Priority
  urgent: { bg: 'var(--danger-dim)', text: 'var(--danger)', border: 'var(--danger-border)' },
  high: { bg: 'var(--warning-dim)', text: 'var(--warning)', border: 'var(--warning-border)' },
  normal: { bg: 'var(--info-dim)', text: 'var(--info)', border: 'var(--info-border)' },
  low: { bg: 'var(--hover-bg)', text: 'var(--ink-tertiary)', border: 'var(--border-subtle)' },
  // Project
  not_started: { bg: 'var(--hover-bg)', text: 'var(--ink-tertiary)', border: 'var(--border-subtle)' },
  planning: { bg: 'var(--info-dim)', text: 'var(--info)', border: 'var(--info-border)' },
  in_progress: { bg: 'var(--accent-dim)', text: 'var(--accent-text)', border: 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)' },
  review: { bg: 'var(--warning-dim)', text: 'var(--warning)', border: 'var(--warning-border)' },
  complete: { bg: 'var(--positive-dim)', text: 'var(--positive)', border: 'var(--positive-border)' },
  on_hold: { bg: 'var(--danger-dim)', text: 'var(--danger)', border: 'var(--danger-border)' },
  cancelled: { bg: 'var(--hover-bg)', text: 'var(--ink-tertiary)', border: 'var(--border-subtle)' },
  // Field job
  scheduled: { bg: 'var(--info-dim)', text: 'var(--info)', border: 'var(--info-border)' },
  en_route: { bg: 'var(--accent-dim)', text: 'var(--accent-text)', border: 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)' },
  on_site: { bg: 'var(--positive-dim)', text: 'var(--positive)', border: 'var(--positive-border)' },
}

export function getStatusColor(status: string) {
  return STATUS_COLORS[status] || STATUS_COLORS.draft
}
